import React, { useMemo } from "react";
import { flattenNestedCalculationTree } from "../utils/nested-calculation-tree";
import {
  defaultPresentationRules,
  normalizePresentationRules,
} from "../utils/presentation-rules-shape";

const MODES = ["show_children", "sum_only", "hide"];
const ACCENT = "#1463C7";

const CALC_DISPLAY_FLAGS = [
  {
    key: "show_hours",
    labelKey: "offers.calculation.nested.display.show_hours",
    hintKey: "offers.calculation.nested.display.show_hours_hint",
  },
  {
    key: "show_quantities",
    labelKey: "offers.calculation.nested.display.show_quantities",
    hintKey: "offers.calculation.nested.display.show_quantities_hint",
  },
  {
    key: "show_products",
    labelKey: "offers.calculation.nested.display.show_products",
    hintKey: "offers.calculation.nested.display.show_products_hint",
  },
  {
    key: "show_setup_only",
    labelKey: "offers.calculation.nested.display.show_setup_only",
    hintKey: "offers.calculation.nested.display.show_setup_only_hint",
  },
  {
    key: "show_quantities_for_offer_item_only",
    labelKey: "offers.calculation.nested.display.show_quantities_offer_item",
    hintKey: "offers.calculation.nested.display.show_quantities_offer_item_hint",
  },
  {
    key: "include_product_short_description",
    labelKey: "offers.calculation.nested.display.include_product_short",
    hintKey: "offers.calculation.nested.display.include_product_short_hint",
  },
];

export { defaultPresentationRules, normalizePresentationRules };

function modeForNode(draft, nodeKey, kind) {
  const hit = (draft?.rules || []).find((rule) => rule.node_key === nodeKey);
  if (hit?.mode) return hit.mode;
  if (kind === "nested_calculation") {
    return draft?.defaults?.nested_calculation || "sum_only";
  }
  return draft?.defaults?.group || "show_children";
}

function setNodeMode(draft, nodeKey, mode, kind) {
  const defaultsMode =
    kind === "nested_calculation"
      ? draft.defaults.nested_calculation
      : draft.defaults.group;
  const without = (draft.rules || []).filter((rule) => rule.node_key !== nodeKey);
  if (mode === defaultsMode) {
    return { ...draft, rules: without };
  }
  return {
    ...draft,
    rules: [
      ...without,
      {
        node_key: nodeKey,
        mode,
        include_nested: true,
        pricing_role: "included",
      },
    ],
  };
}

function FlagRow({ checked, onChange, label, hint }) {
  return (
    <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 rounded border-slate-300"
        style={{ accentColor: ACCENT }}
        checked={Boolean(checked)}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="block font-medium text-slate-800">{label}</span>
        {hint ? (
          <span className="mt-0.5 block text-xs text-slate-500">{hint}</span>
        ) : null}
      </span>
    </label>
  );
}

function ModeSelect({ value, onChange, t, disabled = false }) {
  return (
    <select
      className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-800 focus:border-[#1463C7] focus:outline-none focus:ring-1 focus:ring-[#1463C7] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {MODES.map((mode) => (
        <option key={mode} value={mode}>
          {t(`offers.presentation.mode.${mode}`)}
        </option>
      ))}
    </select>
  );
}

/**
 * Customer/PDF display settings for the focused nested calculation.
 * Reuses offers.presentation_rules + calc show_* + line show_image/url/hours
 * + module note show_in_offer — does not invent a parallel model.
 */
export function NestedCalcDisplayPanel({
  formik,
  t,
  treeRoot = null,
  presentationDraft,
  onPresentationDraftChange,
  canEditPresentation = true,
}) {
  const calcId = formik.values?.id;
  const calcNodeKey = calcId != null ? `calc:${calcId}` : null;
  const draft = presentationDraft || defaultPresentationRules();

  const structureNodes = useMemo(() => {
    if (!calcId) return [];
    const source = treeRoot || formik.values;
    const flat = flattenNestedCalculationTree(source);
    const focus = flat.find((row) => String(row.calc?.id) === String(calcId));
    if (!focus?.calc) return [];

    const nodes = [
      {
        node_key: `calc:${focus.calc.id}`,
        label:
          focus.calc.title ||
          t("offers.calculation.nested.this_calculation"),
        kind: "nested_calculation",
        depth: 0,
        isFocus: true,
      },
    ];

    flat.forEach((row) => {
      if (!row?.calc?.id) return;
      if (String(row.calc.id) === String(calcId)) return;
      // Only descendants under the focused calc (walk parent chain).
      const ancestors = [];
      let cursor = row.parent;
      while (cursor) {
        if (cursor.id != null) ancestors.push(String(cursor.id));
        const parentRow = flat.find(
          (entry) => String(entry.calc?.id) === String(cursor.id)
        );
        cursor = parentRow?.parent || null;
      }
      if (!ancestors.includes(String(calcId))) return;
      nodes.push({
        node_key: `calc:${row.calc.id}`,
        label: row.calc.title || `Kalkyle ${row.calc.id}`,
        kind: "nested_calculation",
        depth: Math.max(1, (row.depth || 1) - (focus.depth || 0)),
        isFocus: false,
      });
    });

    return nodes;
  }, [calcId, formik.values, t, treeRoot]);

  const productLines = useMemo(() => {
    const items = Array.isArray(formik.values?.items) ? formik.values.items : [];
    return items.filter((item) => {
      if (!item || item._destroy) return false;
      if (item.line_type === "product") return true;
      if (item.product_id || item.product) return true;
      if (Array.isArray(item.products) && item.products.length) return true;
      return false;
    });
  }, [formik.values?.items]);

  const noteNodes = useMemo(() => {
    const nodes = formik.values?.module_graph?.nodes;
    if (!Array.isArray(nodes)) return [];
    return nodes.filter((n) => n?.type === "note");
  }, [formik.values?.module_graph?.nodes]);

  const patchPresentation = (next) => {
    onPresentationDraftChange?.(next);
  };

  const setFlag = (key, checked) => {
    formik.setFieldValue(key, checked);
  };

  const patchItem = (rowOrder, patch) => {
    const items = Array.isArray(formik.values?.items)
      ? [...formik.values.items]
      : [];
    const idx = items.findIndex(
      (item) => String(item?.row_order) === String(rowOrder)
    );
    if (idx < 0) return;
    items[idx] = { ...items[idx], ...patch };
    formik.setFieldValue("items", items);
  };

  const patchNoteShow = (nodeId, checked) => {
    const graph = formik.values?.module_graph || { nodes: [], edges: [] };
    const nodes = Array.isArray(graph.nodes) ? graph.nodes : [];
    formik.setFieldValue("module_graph", {
      ...graph,
      nodes: nodes.map((n) =>
        n.id === nodeId
          ? {
              ...n,
              data: {
                ...(n.data || {}),
                show_in_offer: checked,
              },
            }
          : n
      ),
    });
    formik.setFieldValue("module_enabled", true);
  };

  const focusMode = calcNodeKey
    ? modeForNode(draft, calcNodeKey, "nested_calculation")
    : draft.defaults.nested_calculation;

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">
        {t("offers.calculation.nested.display.panel_hint")}
      </p>

      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            {t("offers.calculation.nested.display.structure_heading")}
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {t("offers.calculation.nested.display.structure_hint")}
          </p>
          {!canEditPresentation ? (
            <p className="mt-1 text-xs text-amber-700">
              {t("offers.calculation.nested.display.structure_needs_offer")}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
          <div>
            <div className="text-sm font-medium text-slate-800">
              {t("offers.calculation.nested.this_calculation")}
            </div>
            <div className="text-xs text-slate-400">
              {t("offers.presentation.kind.nested_calculation")}
              {calcNodeKey ? ` · ${calcNodeKey}` : ""}
            </div>
          </div>
          <ModeSelect
            t={t}
            disabled={!canEditPresentation}
            value={focusMode}
            onChange={(mode) => {
              if (!calcNodeKey || !canEditPresentation) return;
              patchPresentation(
                setNodeMode(draft, calcNodeKey, mode, "nested_calculation")
              );
            }}
          />
        </div>

        {structureNodes.filter((n) => !n.isFocus).length > 0 ? (
          <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
            {structureNodes
              .filter((n) => !n.isFocus)
              .map((node) => (
                <li
                  key={node.node_key}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                  style={{ paddingLeft: 12 + (node.depth || 1) * 12 }}
                >
                  <div>
                    <div className="text-sm font-medium text-slate-800">
                      {node.label}
                    </div>
                    <div className="text-xs text-slate-400">
                      {t("offers.presentation.kind.nested_calculation")} ·{" "}
                      {node.node_key}
                    </div>
                  </div>
                  <ModeSelect
                    t={t}
                    disabled={!canEditPresentation}
                    value={modeForNode(draft, node.node_key, node.kind)}
                    onChange={(mode) => {
                      if (!canEditPresentation) return;
                      patchPresentation(
                        setNodeMode(draft, node.node_key, mode, node.kind)
                      );
                    }}
                  />
                </li>
              ))}
          </ul>
        ) : null}

        <FlagRow
          checked={Boolean(draft.defaults.show_product_info)}
          onChange={(checked) => {
            if (!canEditPresentation) return;
            patchPresentation({
              ...draft,
              defaults: { ...draft.defaults, show_product_info: checked },
            });
          }}
          label={t("offers.presentation.show_product_info")}
          hint={t("offers.calculation.nested.display.show_product_info_hint")}
        />
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            {t("offers.calculation.nested.display.content_heading")}
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {t("offers.calculation.nested.display.content_hint")}
          </p>
        </div>
        <div className="grid gap-2">
          {CALC_DISPLAY_FLAGS.map((flag) => (
            <FlagRow
              key={flag.key}
              checked={formik.values?.[flag.key]}
              onChange={(checked) => setFlag(flag.key, checked)}
              label={t(flag.labelKey)}
              hint={t(flag.hintKey)}
            />
          ))}
        </div>
      </section>

      {productLines.length > 0 ? (
        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              {t("offers.calculation.nested.display.products_heading")}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              {t("offers.calculation.nested.display.products_hint")}
            </p>
          </div>
          <ul className="space-y-2">
            {productLines.map((item, index) => {
              const label =
                item.description ||
                item.product?.displayTitle ||
                item.product?.name ||
                t("offers.calculation.nested.display.product_line_fallback", {
                  n: index + 1,
                });
              return (
                <li
                  key={item.id || item.row_order || index}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2.5"
                >
                  <div className="mb-2 text-sm font-medium text-slate-800">
                    {label}
                  </div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    <FlagRow
                      checked={item.show_image !== false}
                      onChange={(checked) =>
                        patchItem(item.row_order, { show_image: checked })
                      }
                      label={t(
                        "offer.calculation.line_editor.show_product_image_pdf"
                      )}
                    />
                    <FlagRow
                      checked={item.show_url !== false}
                      onChange={(checked) =>
                        patchItem(item.row_order, { show_url: checked })
                      }
                      label={t(
                        "offer.calculation.line_editor.show_product_url_pdf"
                      )}
                    />
                    <FlagRow
                      checked={Boolean(item.show_hours)}
                      onChange={(checked) =>
                        patchItem(item.row_order, { show_hours: checked })
                      }
                      label={t(
                        "offers.calculation.nested.display.line_show_hours"
                      )}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {noteNodes.length > 0 ? (
        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              {t("offers.calculation.nested.display.notes_heading")}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              {t("offers.calculation.nested.display.notes_hint")}
            </p>
          </div>
          <div className="grid gap-2">
            {noteNodes.map((node, index) => (
              <FlagRow
                key={node.id}
                checked={node.data?.show_in_offer !== false}
                onChange={(checked) => patchNoteShow(node.id, checked)}
                label={
                  node.title ||
                  t("offers.calculation.nested.notes_item_label", {
                    n: index + 1,
                  })
                }
                hint={t("offers.calculation.nested.notes_show_in_offer_hint")}
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

export default NestedCalcDisplayPanel;
